import React, { useEffect, useState, useRef } from 'react'
import { Form, Checkbox, Spin } from 'antd'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrText from '../WizrText'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrDropDown from '../WizrDropDown'
import { SearchOutlined } from '@ant-design/icons'
import { Input } from 'antd'
import { styled } from 'styled-components'
import { dispatchUploadFileSlackChannels, dispatchIntegrationList } from '../../redux/actions/content'
import { useGetIntegrationList, useUploadFilesSlackChannelsSelector } from '../../redux/selectors/content'
import Circlecross from '../../assets/icons/Circlecross'

const { Search } = Input

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
  width: 100%;
  position: relative;
`

const StyledForm = styled(Form)`
  width: 100%;
`

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
  width: 100%;
`

const StyledDropDown = styled(WizrDropDown)`
  height: 52px;
  .ant-select-selector {
    padding-top: 0px !important;
    padding-left: 16px !important;
    border-radius: 16px;
    display: flex;
    align-items: center;
    .ant-select-selection-placeholder {
      margin: 0px !important;
      text-align: left !important;
      line-height: 52px !important;
    }
  }
`

const ChannelTag = styled.div`
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #707276;
  border-radius: 20px;
  padding: 0px 12px;
  margin: 2px;
  gap: 8px;
  position: relative;
`

const DropdownContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

const SearchContainer = styled.div`
  margin-bottom: 4px;
  padding: 8px;
  
  .ant-input-search {
    .ant-input {
      border-radius: 4px;
      padding: 4px 8px 4px 32px;
      height: 28px;
      font-family: 'Montserrat', sans-serif;
      
      &::placeholder {
        font-family: 'Montserrat', sans-serif;
      }
    }

    .ant-input-search-button {
      display: none;
    }
  }
`

const CheckboxList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 8px;
  
  .checkbox-item {
    padding: 8px;
  }
`

const DropdownFooter = styled.div`
  border-top: 1px solid #e8e8e8;
  padding: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

// const LoadMoreButton = styled.button`
//   background: none;
//   border: none;
//   color: #595959;
//   text-decoration: underline;
//   cursor: pointer;
//   font-size: 16px;
//   padding: 0px 8px;
//   width: 100%;
//   text-align: center;

//   &:hover {
//     color: #404040;
//   }
// `

interface IUploadSlackForm {
  onFormSubmission: (values: any) => void
  onCancel: () => void
  folderId: string
}

const UploadSlackForm: React.FC<IUploadSlackForm> = ({
  onFormSubmission,
  onCancel,
  folderId,
}) => {
  const [form] = Form.useForm()
  const [selectedChannels, setSelectedChannels] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [availableChannels, setAvailableChannels] = useState<any[]>([])
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  // Validation state
  const [showIntegrationValidation, setShowIntegrationValidation] = useState(false)
  const [showChannelsValidation, setShowChannelsValidation] = useState(false)

  // Refs for error scrolling
  const integrationRef = useRef<HTMLDivElement>(null)
  const channelsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatchIntegrationList()
  }, [])

  const dropDownConverterIntegrations = (value: any[]): { label: string; value: string }[] => {
    if (!value?.length) return []

    const slackIntegrations = value.filter(item =>
      item?.integration_key?.toLowerCase() === 'slack'
    )

    return slackIntegrations.map((item: any) => ({
      label: item?.display_name,
      value: item.id,
    }))
  }

  const channelsOfSlack = useUploadFilesSlackChannelsSelector()

  useEffect(() => {
    if (channelsOfSlack?.channels) {
      setAvailableChannels(prevChannels => [...prevChannels, ...channelsOfSlack.channels])
    }
  }, [channelsOfSlack])

  const filteredChannels = availableChannels.filter((channel: any) =>
    channel.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleChannelToggle = (channelId: string) => {
    const newSelectedChannels = selectedChannels.includes(channelId)
      ? selectedChannels.filter(ch => ch !== channelId)
      : [...selectedChannels, channelId]

    setSelectedChannels(newSelectedChannels)
    form.setFieldValue('channels', newSelectedChannels)

    if (newSelectedChannels.length > 0 && showChannelsValidation) {
      setShowChannelsValidation(false)
    }
  }

  const handleReset = () => {
    setSelectedChannels([])
    setSearchTerm('')
    form.setFieldValue('channels', [])
  }

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDropdownVisible(false)
  }

  const integrationsForTools = useGetIntegrationList()
  const options = dropDownConverterIntegrations(integrationsForTools)

  const handleIntegrationChange = async (value: any) => {
    setSelectedChannels([])
    setAvailableChannels([])
    setSelectedIntegration(value)
    form.setFieldValue('channels', [])

    if (value && showIntegrationValidation) {
      setShowIntegrationValidation(false)
    }

    if (value) {
      await loadMoreChannels(value, null)
    }
  }

  const loadMoreChannels = async (integrationId: string, nextCursor: string | null) => {
    setLoadingMore(true)
    const payload: any = { integration_id: integrationId }
    if (nextCursor) {
      payload.next_cursor = nextCursor
    }
    await dispatchUploadFileSlackChannels(payload)
    setLoadingMore(false)
  }

  // const handleLoadMore = async () => {
  //   if (channelsOfSlack?.has_more && selectedIntegration) {
  //     await loadMoreChannels(selectedIntegration, channelsOfSlack.next_cursor)
  //   }
  // }

  const validateAllFields = () => {
    const isBlank = (text?: string | null) => !text || text.trim() === ''
    let isValid = true
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null

    setShowIntegrationValidation(false)
    setShowChannelsValidation(false)

    if (isBlank(selectedIntegration)) {
      setShowIntegrationValidation(true)
      if (!firstErrorRef) firstErrorRef = integrationRef
      isValid = false
    }

    if (!selectedChannels || selectedChannels.length === 0) {
      setShowChannelsValidation(true)
      if (!firstErrorRef) firstErrorRef = channelsRef
      isValid = false
    }

    if (!isValid && firstErrorRef?.current) {
      setTimeout(() => {
        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    return isValid
  }

  const handleSubmit = () => {
    if (!validateAllFields()) {
      return
    }

    onFormSubmission({
      integration_id: selectedIntegration,
      channels: selectedChannels,
      availableChannels: availableChannels,
    })
  }

  const handleTagClose = (removedChannel: string) => {
    const newSelectedChannels = selectedChannels.filter(channel => channel !== removedChannel)
    setSelectedChannels(newSelectedChannels)
    form.setFieldValue('channels', newSelectedChannels)
  }

  const TagRender = ({ value, closable, onClose }: { value: string; closable?: boolean; onClose?: () => void }) => {
    const channelName = availableChannels.find((channel: any) => channel.id === value)?.name || value

    return (
      <ChannelTag>
        {channelName}
        {closable && (
          <span
            className="close-icon"
            onClick={(e) => {
              e.preventDefault()
              handleTagClose(value)
            }}
          >
            <Circlecross />
          </span>
        )}
      </ChannelTag>
    )
  }

  return (
    <StyledContainer background="#FFF" justifyContent="space-evenly">
      <div style={{ width: '100%', position: 'relative' }}>
        <WizrFlexLayout
          flexDirection="column"
          justifyContent="flex-start"
          background="#fff"
          alignItems="flex-start"
          style={{ width: '100%', gap: '16px' }}
        >
          <StyledForm
            name="slack"
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFinishFailed={() => null}
            autoComplete="off"
            style={{ width: '100%' }}
          >
            <Form.Item name="integration_id">
              <InputContainer ref={integrationRef}>
                <WizrText type="body2">Select Integration</WizrText>
                <StyledDropDown
                  value={selectedIntegration || undefined}
                  options={options}
                  placeHolder="Select Slack Integration"
                  onChange={(value: any) => {
                    handleIntegrationChange(value)
                  }}
                  showError={showIntegrationValidation}
                  errorMessage={showIntegrationValidation ? 'Please Select an Integration' : undefined}
                />
              </InputContainer>
            </Form.Item>

            {selectedIntegration && channelsOfSlack?.channels && (
              <Form.Item name="channels">
                <InputContainer ref={channelsRef}>
                  <WizrText type="body2">Slack Channels</WizrText>
                  <StyledDropDown
                    placeHolder="Select Slack Channels"
                    mode="multiple"
                    value={selectedChannels}
                    open={isDropdownVisible}
                    onDropdownVisibleChange={(visible) => { if (visible) setIsDropdownVisible(true) }}
                    tagRender={TagRender}
                    labelInValue={false}
                    maxTagCount="responsive"
                    options={availableChannels.map((channel: any) => ({
                      label: channel.name,
                      value: channel.id,
                    }))}
                    dropdownStyle={{
                      padding: 0,
                      width: '28%',
                      minWidth: '28%',
                      zIndex: 1001,
                    }}
                    dropdownAlign={{
                      points: ['tr', 'br'],
                      offset: [0, 4],
                    }}
                    dropdownRender={(menu) => (
                      <DropdownContainer>
                        <SearchContainer>
                          <Search
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            prefix={<SearchOutlined style={{ color: '#8C8C8C' }} />}
                            allowClear={false}
                          />
                        </SearchContainer>

                        <CheckboxList>
                          {filteredChannels.map((channel: any) => (
                            <div className="checkbox-item" key={channel.id}>
                              <Checkbox
                                checked={selectedChannels.includes(channel.id)}
                                onChange={() => handleChannelToggle(channel.id)}
                              >
                                {channel.name}
                              </Checkbox>
                            </div>
                          ))}
                        </CheckboxList>

                        {/* {channelsOfSlack?.has_more && (
                          <div style={{ textAlign: 'center', padding: '8px' }}>
                            {loadingMore ? <Spin /> : <LoadMoreButton onClick={handleLoadMore}>Load more</LoadMoreButton>}
                          </div>
                        )} */}

                        <DropdownFooter>
                          <button
                            type="button"
                            style={{
                              width: '68px',
                              height: '28px',
                              borderRadius: '12px',
                              border: '1px solid #212121',
                              background: 'white',
                              color: '#1D1F22',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                            onClick={handleReset}
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            style={{
                              width: '65px',
                              height: '28px',
                              borderRadius: '12px',
                              border: '1px solid #212121',
                              background: '#1D1F22',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                            onClick={handleAdd}
                          >
                            Add
                          </button>
                        </DropdownFooter>
                      </DropdownContainer>
                    )}
                    showError={showChannelsValidation}
                    errorMessage={showChannelsValidation ? 'Please Select at Least One Channel' : undefined}
                  />
                </InputContainer>
              </Form.Item>
            )}

            <WizrFlexSpacing
              justifyContent="flex-end"
              flexDirection="row"
              size="large"
              background="#FFF"
            >
              <WizrButton type="default" text="Cancel" onClick={onCancel} style={{ borderRadius: '20px' }} />
              <WizrButton text="Add Slack" onClick={handleSubmit} style={{ borderRadius: '20px' }} />
            </WizrFlexSpacing>
          </StyledForm>
        </WizrFlexLayout>
      </div>
    </StyledContainer>
  )
}

export default UploadSlackForm